import { createEffect, createEvent, createStore, sample } from "effector";
import { residentialComplexesApi } from "../api/residentialComplexes";
import { BuildingInDB, ResidentialComplexRead } from "../api/types";
import { Building, createBuildingFx } from "./buildings";
import { $canDeleteComplex } from "./auth";
import { setActiveTenantId, $activeTenantId } from "./tenantContext";

type AnyBuilding = Building | BuildingInDB;

/** Загрузка списка */
export const fetchResidentialComplexesFx = createEffect(async () => {
  const data = await residentialComplexesApi.getResidentialComplexes(0, 100);
  return data;
});

/** Удаление на бэке */
export const deleteResidentialComplexFx = createEffect(
  async ({ id, force = false }: { id: string; force?: boolean }) => {
    await residentialComplexesApi.deleteResidentialComplex(id, force);
    return id;
  }
);

/** Клик по кнопке удаления из любой точки UI */
export const deleteComplexClicked = createEvent<{ id: string; force?: boolean }>();

/**
 * ВАЖНО: вместо guard используем sample.
 * Если у пользователя есть право ($canDeleteComplex === true),
 * пробрасываем payload (id) из клика прямо в эффект удаления.
 */
sample({
  clock: deleteComplexClicked,
  filter: $canDeleteComplex,
  target: deleteResidentialComplexFx,
});

/** Трекаем id, которые сейчас удаляются (для дизейбла/спиннера) */
export const $deletingIds = createStore<Set<string>>(new Set())
  .on(deleteResidentialComplexFx, (set, payload) => {
    const next = new Set(set);
    next.add(String(payload.id));
    return next;
  })
  .on(deleteResidentialComplexFx.finally, (set, { params }) => {
    const next = new Set(set);
    next.delete(String(params.id));
    return next;
  });

/** Основной список ЖК */
export const $complexes = createStore<ResidentialComplexRead[]>([])
  .on(fetchResidentialComplexesFx.doneData, (_, data) =>
    Array.isArray(data) ? data : []
  )
  .on(createBuildingFx.doneData, (state, { complexId, building }) => {
    const idx = state.findIndex((c) => String(c.id) === String(complexId));
    if (idx === -1) return state;

    const next = state.slice();
    const target = next[idx];

    const prevBuildings: AnyBuilding[] = Array.isArray(
      (target as any).buildings
    )
      ? ((target as any).buildings as AnyBuilding[])
      : [];

    next[idx] = {
      ...target,
      buildings: [building as AnyBuilding, ...prevBuildings],
    } as ResidentialComplexRead;

    return next;
  })
  .on(deleteResidentialComplexFx.doneData, (state, complexId) =>
    state.filter((c) => String(c.id) !== String(complexId))
  );
  
$complexes.reset(setActiveTenantId);

/** Индикаторы загрузки/ошибки для списка (без изменений) */
export const $complexesLoading = createStore<boolean>(false)
  .on(fetchResidentialComplexesFx, () => true)
  .on(fetchResidentialComplexesFx.done, () => false)
  .on(fetchResidentialComplexesFx.fail, () => false);

export const $complexesError = createStore<string | null>(null)
  .on(
    fetchResidentialComplexesFx.failData,
    (_, err: any) => err?.message || "Ошибка загрузки объектов"
  )
  .on(fetchResidentialComplexesFx, () => null)
  .on(fetchResidentialComplexesFx.done, () => null);

$complexesError.reset(setActiveTenantId);

export const complexSelected = createEvent<string>(); // клик по объекту
export const $activeComplexId = createStore<string | null>(null).on(
  complexSelected,
  (_, id) => String(id)
);

$activeComplexId.reset(setActiveTenantId);

sample({
  clock: $activeTenantId.updates,
  filter: (tenantId) => Boolean(tenantId),
  target: fetchResidentialComplexesFx,
});
sample({
  clock: fetchResidentialComplexesFx.doneData,
  source: $activeComplexId,
  fn(activeId, list) {
    if (!activeId) return null;
    const exists = Array.isArray(list) && list.some(c => String(c.id) === String(activeId));
    return exists ? activeId : null;
  },
  target: $activeComplexId,
});
